import type { CreateLabelRuleBody } from '@ipfabric/api-client/client/schemas';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Filters } from '@ipfabric/utils/table/filters/filters';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'utils/request/useApi';

export type CreateRuleBody = CreateLabelRuleBody;
export type UpdateRuleBody = Omit<CreateLabelRuleBody, 'type'>;
export type RuleLabel = CreateLabelRuleBody['labels'][number];

export type AutoAssignmentRule = {
  id: string;
  type: CreateLabelRuleBody['type'];
  description: string;
  filter: Filters;
  labels: RuleLabel[];
  inherit: boolean;
  createdBy: string | null;
  createdAt: number;
  changedAt: number;
  changedBy: string | null;
};

const RULES_REQUEST_PARAMS = { method: 'GET' as const, url: '/labels/rules' };

export const useRulesQuery = () => {
  const api = useApi();
  return useQuery(api.query<AutoAssignmentRule[]>(RULES_REQUEST_PARAMS));
};

export const useInvalidateRules = () => {
  const queryClient = useQueryClient();
  return useStableCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [RULES_REQUEST_PARAMS],
    });
  });
};

export const useRulesApi = () => {
  const api = useApi();

  const createRule = useStableCallback(async (body: CreateRuleBody) => {
    await api.request<void>({
      method: 'POST',
      url: '/labels/rules',
      data: body,
    });
  });

  const updateRule = useStableCallback(
    async (id: string, body: UpdateRuleBody) => {
      await api.request<void>({
        method: 'PUT',
        url: `/labels/rules/${id}`,
        data: body,
      });
    },
  );

  const deleteRule = useStableCallback(async (id: string) =>
    api.request<void>({
      method: 'DELETE',
      url: `/labels/rules/${id}`,
    }),
  );

  return { createRule, updateRule, deleteRule };
};
